/** @file Issues every chunk's draws in order; page and blend state switch only when they change. */
import { RENDER_LAYOUT } from '@pose2d/geometry/renderBuffers';
import type { RenderBuffers } from '@pose2d/geometry/types';
import { invariant } from '@pose2d/std/invariant';
import { BLEND_FACTORS } from './blendFactors.data';
import type { ChunkStore } from './createChunkStore';
import type { PageStore } from './createPageStore';

const DS = RENDER_LAYOUT.DRAW_STRIDE;
const at = (table: Int32Array, i: number): number => table[i] ?? 0;

/** Sets blendFuncSeparate for a Blend code; a code outside 0..3 is a programming error. */
function setBlend(gl: WebGL2RenderingContext, blend: number): void {
  const row = BLEND_FACTORS[blend];
  invariant(row, `draw blend ${blend} is not 0..3`);
  gl.blendFuncSeparate(row[0], row[1], row[2], row[3]);
}

/** Draws chunks in order, each chunk's draw table in order (index order == draw order, §7.1). */
export function issueDraws(gl: WebGL2RenderingContext, chunks: readonly RenderBuffers[], store: ChunkStore, pages: PageStore): void {
  let page = -1;
  let blend = -1;
  chunks.forEach((chunk, c) => {
    if (chunk.drawCount === 0) return;
    const indexByte = store.bindChunk(c);
    for (let k = 0; k < chunk.drawCount; k++) {
      const o = k * DS;
      const p = at(chunk.draws, o);
      const b = at(chunk.draws, o + 1);
      const count = at(chunk.draws, o + 3);
      if (p !== page) { pages.bind(p); page = p; }
      if (b !== blend) { setBlend(gl, b); blend = b; }
      if (count > 0) gl.drawElements(gl.TRIANGLES, count, gl.UNSIGNED_SHORT, indexByte + at(chunk.draws, o + 2) * Uint16Array.BYTES_PER_ELEMENT);
    }
  });
}
